import { useState } from 'react';
import clsx from 'clsx';
import SideDrawer from '../../../../../common/_custom/SideDrawer/SideDrawer';
import { purpleRedirectIcon } from '../../../../../assets/hostedassets';
import { cellBandClass } from '../../../components/Reconciliations/components/ReconCellContent';
import ToleranceLegend from '../../../components/Reconciliations/components/ToleranceLegend';
import EntityDrillDownDrawer from '../../Financials/components/FinancialsFromBankingV2/EntityDrillDownDrawer';
import { useLatestRequestGuard } from '../../../../../common/hooks/useLatestRequestGuard';
import type { BankTransaction } from '../../Financials/components/FinancialsFromBankingV2/types';
import type { ReconColumn } from '../../../../../store/reconciliations/types';
import { fetchRelatedPartyBankTransactions } from '../../../../../store/dashboard/relatedParties/relatedParties_apiHelper';
import { formatDrawerAmount, formatFy } from '../../../../../store/dashboard/relatedParties/relatedPartyTransactions_mappers';
import type { RptParty, RptPartyRow, RptTolerance } from '../../../../../store/dashboard/relatedParties/relatedPartyTransactions.types';
import {
    RPT_BANK_TXNS_BANNER,
    RPT_BANKING_UNTAGGED_NOTE,
    RPT_DRAWER_COLUMNS,
    RPT_DRAWER_TITLE,
    RPT_EMPTY_VALUE,
    RPT_GST_UNTAGGED_NOTE,
    RPT_NOT_DISCLOSED,
    RPT_MCA_NOT_REPORTED_NOTE,
} from './relatedPartyTransactions_constants';
import styles from './RelatedPartyTransactionsDrawer.module.scss';

const COMPARED_COLUMN: ReconColumn = { key: 'compared', label: '', role: 'compared' };
const BANKING_COLUMN: ReconColumn = { key: 'banking', label: RPT_DRAWER_COLUMNS.BANKING, role: 'anchor' };

const amountText = (value: number | null): string => (value === null ? RPT_EMPTY_VALUE : formatDrawerAmount(value));

const mcaText = (row: RptPartyRow): string => (row.mca === null && row.mcaRaw ? RPT_NOT_DISCLOSED : amountText(row.mca));

const amountClass = (value: number | null): string => clsx(styles.Amount, { [styles.Amount_Negative]: value !== null && value < 0 });

function ComparedCell({ text, value, tolerance }: { text: string; value: number | null; tolerance: RptTolerance | null }) {
    const bandClass = tolerance ? cellBandClass({ value, score: tolerance.score, status: tolerance.status }, COMPARED_COLUMN) : undefined;
    return (
        <td className={bandClass}>
            <span className={styles.AmountCellInner}>
                {tolerance && <span className={styles.Chip}>{`${tolerance.deviationPct}%`}</span>}
                <span className={amountClass(value)}>{text}</span>
            </span>
        </td>
    );
}

function RelatedPartyTransactionsDrawer({ orgId, party, onClose }: { orgId: string; party: RptParty; onClose: () => void }) {
    const [bankDrawer, setBankDrawer] = useState<{ loading: boolean; transactions: BankTransaction[] } | null>(null);
    const bankRequestGuard = useLatestRequestGuard();

    const openBankTransactions = async () => {
        const token = bankRequestGuard.startRequest();
        setBankDrawer({ loading: true, transactions: [] });
        const transactions = await fetchRelatedPartyBankTransactions(orgId, party.partyKey);
        if (!bankRequestGuard.isCurrent(token)) return;
        setBankDrawer((current) => (current ? { loading: false, transactions } : current));
    };

    const showBankingNote = party.rows.every((row) => row.banking === null);
    const showGstNote = party.rows.every((row) => row.gst === null);
    const showMcaNote = party.transactionCount === 0;
    const notes = [
        showMcaNote && RPT_MCA_NOT_REPORTED_NOTE,
        showBankingNote && RPT_BANKING_UNTAGGED_NOTE,
        showGstNote && RPT_GST_UNTAGGED_NOTE,
    ].filter(Boolean) as string[];

    const heading = (
        <span className={styles.Heading}>
            <strong className={styles.HeadingTitle}>{RPT_DRAWER_TITLE}</strong>
            <span className={styles.HeadingDivider} aria-hidden="true" />
            <span className={styles.HeadingName}>{party.partyName}</span>
        </span>
    );

    return (
        <>
            <SideDrawer
                width="70%"
                showHeading
                heading={heading}
                headerVariant="tight"
                headerActions={<ToleranceLegend bothDirections />}
                ariaLabel={`${RPT_DRAWER_TITLE} ${party.partyName}`}
                onClose={onClose}
            >
                <div className={styles.Body}>
                    {!showBankingNote && (
                        <button type="button" className={styles.Banner} onClick={openBankTransactions}>
                            <img src={purpleRedirectIcon} alt="" className={styles.BannerIcon} />
                            <span>{RPT_BANK_TXNS_BANNER}</span>
                        </button>
                    )}
                    <div className={styles.Content}>
                        <div className={styles.TableWrap}>
                            <table className={styles.Table}>
                                <thead>
                                    <tr>
                                        <th className={styles.FyHeader}>{RPT_DRAWER_COLUMNS.FY}</th>
                                        <th className={styles.TypeHeader}>{RPT_DRAWER_COLUMNS.TYPE}</th>
                                        <th className={styles.AmountHeader}>{RPT_DRAWER_COLUMNS.MCA}</th>
                                        <th className={styles.AmountHeader}>{RPT_DRAWER_COLUMNS.GST}</th>
                                        <th className={styles.AmountHeader}>{RPT_DRAWER_COLUMNS.BANKING}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {party.rows.map((row, index) => (
                                        <tr
                                            key={`${row.financialYear}-${row.transactionType}`}
                                            className={clsx({
                                                [styles.FyGroupEnd]:
                                                    index < party.rows.length - 1 &&
                                                    party.rows[index + 1].financialYear !== row.financialYear,
                                            })}
                                        >
                                            <td className={styles.FyCell}>{formatFy(row.financialYear)}</td>
                                            <td className={styles.TypeCell}>{row.transactionType}</td>
                                            <ComparedCell text={mcaText(row)} value={row.mca} tolerance={row.mcaTolerance} />
                                            <ComparedCell text={amountText(row.gst)} value={row.gst} tolerance={row.gstTolerance} />
                                            <td className={cellBandClass(undefined, BANKING_COLUMN)}>
                                                <span className={styles.AmountCellInner}>
                                                    <span className={amountClass(row.banking)}>{amountText(row.banking)}</span>
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {notes.length > 0 && (
                            <ul className={styles.NoteList}>
                                {notes.map((note) => (
                                    <li key={note} className={styles.Note}>
                                        {note}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </SideDrawer>
            {bankDrawer && (
                <EntityDrillDownDrawer
                    entityName={party.partyName}
                    transactions={bankDrawer.transactions}
                    loading={bankDrawer.loading}
                    onClose={() => setBankDrawer(null)}
                />
            )}
        </>
    );
}

export default RelatedPartyTransactionsDrawer;
